/**
 * @fileoverview Text-to-speech hook using Web Speech API
 *
 * Provides text-to-speech functionality for reading command responses
 * and other system messages aloud.
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { speakText, getAvailableVoices, getTTSHealth } from '../api/tts';

// Debug flag - set to true to enable console logging
const DEBUG = false;
const log = (...args) => DEBUG && console.log(...args);

export function useTextToSpeech(options = {}) {
  const {
    defaultVoice = 'Joanna-generative', // Default to Polly generative voice
    defaultRate = 1.0,
    defaultPitch = 1.0,
    defaultVolume = 1.0,
    preferPolly = true // Prefer Polly over Web Speech API
  } = options;

  const [isSupported, setIsSupported] = useState(false);
  const [isPollySupported, setIsPollySupported] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [voices, setVoices] = useState([]);
  const [pollyVoices, setPollyVoices] = useState({});
  const [currentVoice, setCurrentVoice] = useState(defaultVoice);
  const [rate, setRate] = useState(defaultRate);
  const [pitch, setPitch] = useState(defaultPitch);
  const [volume, setVolume] = useState(defaultVolume);
  const [error, setError] = useState(null);
  const [usingPolly, setUsingPolly] = useState(preferPolly);

  const synthRef = useRef(null);
  const currentUtteranceRef = useRef(null);
  const currentAudioRef = useRef(null);

  // Check for Polly and Web Speech API support
  useEffect(() => {
    const initializeTTS = async () => {
      let pollyAvailable = false;
      let webSpeechAvailable = false;

      // Check Polly support
      try {
        const healthCheck = await getTTSHealth();
        if (healthCheck.hasPermissions !== false) {
          const voicesData = await getAvailableVoices();
          setIsPollySupported(true);
          setPollyVoices(voicesData.voices);
          pollyAvailable = true;
          log('✅ Amazon Polly TTS available');
        } else {
          throw new Error('AWS Polly permissions not configured');
        }
      } catch (error) {
        console.warn('⚠️ Amazon Polly TTS not available:', error.message);
        setIsPollySupported(false);
        setUsingPolly(false);
      }

      // Check Web Speech API support
      if ('speechSynthesis' in window) {
        synthRef.current = window.speechSynthesis;
        webSpeechAvailable = true;

        // Load available voices
        const loadVoices = () => {
          const availableVoices = synthRef.current.getVoices();
          setVoices(availableVoices);

          // Set default voice if not already set and not using Polly
          if (!currentVoice && availableVoices.length > 0 && !pollyAvailable) {
            // Try to find a good default voice (preferably English)
            const englishVoice = availableVoices.find(voice =>
              voice.lang.startsWith('en') && voice.localService
            ) || availableVoices.find(voice =>
              voice.lang.startsWith('en')
            ) || availableVoices[0];

            setCurrentVoice(englishVoice);
          }
        };

        // Load voices immediately
        loadVoices();

        // Some browsers load voices asynchronously
        if (synthRef.current.onvoiceschanged !== undefined) {
          synthRef.current.onvoiceschanged = loadVoices;
        }

        log('✅ Web Speech API TTS available');
      }

      // Set overall support based on available options
      const anyTTSSupported = pollyAvailable || webSpeechAvailable;
      setIsSupported(anyTTSSupported);

      if (!anyTTSSupported) {
        setError('Text-to-speech is not supported in this browser.');
      }
    };

    initializeTTS();

    return () => {
      if (synthRef.current) {
        synthRef.current.cancel();
      }
      if (currentAudioRef.current) {
        currentAudioRef.current.pause();
        currentAudioRef.current.src = '';
      }
    };
  }, []);

  const speak = useCallback(async (text, options = {}) => {
    if (!isSupported || !text?.trim()) {
      return;
    }

    // Stop any current speech
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current.src = '';
      currentAudioRef.current = null;
    }
    if (synthRef.current) {
      synthRef.current.cancel();
      currentUtteranceRef.current = null;
    }

    try {
      setIsSpeaking(true);
      setError(null);

      if (usingPolly && isPollySupported) {
        // Use Amazon Polly
        log('🔊 Starting Polly speech synthesis:', text.substring(0, 50) + '...');

        const pollyOptions = {
          voice: currentVoice,
          speed: rateToPollySpeed(options.rate || rate),
          volume: options.volume || volume,
          format: 'mp3'
        };

        const audioElement = await speakText(text, pollyOptions);
        currentAudioRef.current = audioElement;

        // Set up event handlers for Polly audio
        audioElement.onended = () => {
          setIsSpeaking(false);
          setIsPaused(false);
          currentAudioRef.current = null;
          log('🔇 Polly speech finished');
        };

        audioElement.onerror = (error) => {
          setIsSpeaking(false);
          setIsPaused(false);
          setError('Polly speech playback failed');
          currentAudioRef.current = null;
          console.error('Polly speech error:', error);
        };

      } else if (synthRef.current) {
        // Fallback to Web Speech API
        log('🔊 Starting Web Speech API synthesis:', text.substring(0, 50) + '...');

        const utterance = new SpeechSynthesisUtterance(text);

        // Apply voice settings
        if (currentVoice && typeof currentVoice === 'object') {
          utterance.voice = currentVoice;
        }
        utterance.rate = options.rate || rate;
        utterance.pitch = options.pitch || pitch;
        utterance.volume = options.volume || volume;

        // Set up event handlers
        utterance.onstart = () => {
          log('🔊 Web Speech API started speaking');
        };

        utterance.onend = () => {
          setIsSpeaking(false);
          setIsPaused(false);
          currentUtteranceRef.current = null;
          log('🔇 Web Speech API finished speaking');
        };

        utterance.onerror = (event) => {
          setIsSpeaking(false);
          setIsPaused(false);
          setError(`Speech error: ${event.error}`);
          currentUtteranceRef.current = null;
          console.error('Web Speech API error:', event.error);
        };

        utterance.onpause = () => {
          setIsPaused(true);
        };

        utterance.onresume = () => {
          setIsPaused(false);
        };

        // Store reference and speak
        currentUtteranceRef.current = utterance;
        synthRef.current.speak(utterance);
      }
    } catch (error) {
      setIsSpeaking(false);
      setIsPaused(false);
      setError(error.message);
      console.error('Speech synthesis failed:', error);

      // Try fallback to Web Speech API if Polly fails
      if (usingPolly && synthRef.current) {
        log('🔄 Polly failed, falling back to Web Speech API');
        setUsingPolly(false);
        // Retry with Web Speech API
        setTimeout(() => speak(text, options), 100);
      }
    }
  }, [isSupported, isPollySupported, usingPolly, currentVoice, rate, pitch, volume]);

  // Helper function to convert rate to Polly speed
  const rateToPollySpeed = (rate) => {
    if (rate <= 0.5) return 'x-slow';
    if (rate <= 0.75) return 'slow';
    if (rate <= 1.25) return 'medium';
    if (rate <= 1.5) return 'fast';
    return 'x-fast';
  };

  const stop = useCallback(() => {
    // Stop Polly audio if playing
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current.src = '';
      currentAudioRef.current = null;
    }

    // Stop Web Speech API if active
    if (synthRef.current) {
      synthRef.current.cancel();
      currentUtteranceRef.current = null;
    }

    setIsSpeaking(false);
    setIsPaused(false);
  }, []);

  const pause = useCallback(() => {
    if (currentAudioRef.current && isSpeaking && !isPaused) {
      currentAudioRef.current.pause();
      setIsPaused(true);
    } else if (synthRef.current && isSpeaking && !isPaused) {
      synthRef.current.pause();
      setIsPaused(true);
    }
  }, [isSpeaking, isPaused]);

  const resume = useCallback(() => {
    if (currentAudioRef.current && isSpeaking && isPaused) {
      currentAudioRef.current.play();
      setIsPaused(false);
    } else if (synthRef.current && isSpeaking && isPaused) {
      synthRef.current.resume();
      setIsPaused(false);
    }
  }, [isSpeaking, isPaused]);

  // Convenience method for reading command responses
  const speakResponse = useCallback((response, options = {}) => {
    if (!response) return;

    let textToSpeak = '';

    if (typeof response === 'string') {
      textToSpeak = response;
    } else if (response.explanation) {
      textToSpeak = response.explanation;
    } else if (response.message) {
      textToSpeak = response.message;
    } else if (response.error) {
      textToSpeak = `Error: ${response.error}`;
    }

    if (textToSpeak) {
      speak(textToSpeak, {
        rate: 1.1, // Slightly faster for responses
        ...options
      });
    }
  }, [speak]);

  return {
    // State
    isSupported,
    isPollySupported,
    isSpeaking,
    isPaused,
    voices,
    pollyVoices,
    currentVoice,
    rate,
    pitch,
    volume,
    error,
    usingPolly,

    // Settings
    setCurrentVoice,
    setRate,
    setPitch,
    setVolume,
    setUsingPolly,

    // Actions
    speak,
    stop,
    pause,
    resume,
    speakResponse,

    // Status helpers
    isActive: isSpeaking,
    canPause: isSpeaking && !isPaused,
    canResume: isSpeaking && isPaused,
    hasError: Boolean(error),
    provider: usingPolly && isPollySupported ? 'polly' : 'webspeech'
  };
}